import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, Scissors, Send } from "lucide-react";
import { Logo, Eyebrow, Reveal } from "@/components/Reveal";
import ThemeToggle from "@/components/ThemeToggle";
import Seo from "@/components/Seo";
import { api } from "@/lib/api";

const inputCls =
    "w-full bg-paper border border-line rounded-md px-3 py-2.5 text-sm text-ink focus:outline-none focus:border-copper transition-colors duration-300";
const labelCls = "font-mono text-[10px] uppercase tracking-[0.2em] text-dim block mb-1.5";

export default function CableCutting() {
    const [products, setProducts] = useState([]);
    const [sku, setSku] = useState("");
    const [cuts, setCuts] = useState([{ length: "", qty: "1" }]);
    const [rate, setRate] = useState("550");
    const [plan, setPlan] = useState(null);
    const [planning, setPlanning] = useState(false);
    const [contact, setContact] = useState({ name: "", company: "", phone: "", email: "", note: "" });
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(null);

    useEffect(() => {
        api.get("/products", { params: { limit: 50 } })
            .then((res) => setProducts(res.data.filter((p) => !["Glands & Lugs", "Cable Jointing Kits"].includes(p.category))))
            .catch(() => setProducts([]));
    }, []);

    const total = useMemo(
        () => cuts.reduce((s, c) => s + (parseFloat(c.length) || 0) * (parseInt(c.qty) || 0), 0),
        [cuts]
    );

    const setCut = (i, k, v) => {
        setCuts((p) => p.map((c, idx) => (idx === i ? { ...c, [k]: v } : c)));
        setPlan(null);
    };

    const validCuts = cuts.filter((c) => parseFloat(c.length) > 0 && parseInt(c.qty) > 0);

    const runPlan = async () => {
        if (!sku) return toast.error("Select a cable product first");
        if (!validCuts.length) return toast.error("Add at least one cut length");
        setPlanning(true);
        try {
            const res = await api.post("/cutting/plan", {
                sku,
                cuts: validCuts.map((c) => ({ length: parseFloat(c.length), qty: parseInt(c.qty) })),
            });
            setPlan(res.data);
        } catch (e) {
            const d = e.response?.data?.detail;
            toast.error(typeof d === "string" ? d : "Could not plan cuts");
        } finally {
            setPlanning(false);
        }
    };

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const res = await api.post("/cutting/submit", {
                sku,
                cuts: validCuts.map((c) => ({ length: parseFloat(c.length), qty: parseInt(c.qty) })),
                rate: parseFloat(rate) || null,
                ...contact,
            });
            setDone(res.data);
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch (err) {
            const d = err.response?.data?.detail;
            toast.error(typeof d === "string" ? d : "Submission failed — try again");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-paper text-ink" data-testid="cutting-page">
            <Seo
                title="Cable Cutting to Length in Bengaluru"
                description="Order cables cut to your exact lengths from drum stock at Roxy Cables, Chickpet, Bengaluru — plan cuts, see drum allocation and submit your requirement."
                path="/cutting"
            />
            <header className="sticky top-0 z-30 backdrop-blur-xl bg-paper/85 border-b border-line">
                <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Logo size={30} />
                        <span className="font-display font-bold tracking-[0.06em] text-lg text-ink hidden sm:block">Roxy Cables</span>
                        <span className="font-mono text-[10px] tracking-[0.25em] text-copper uppercase ml-2">Cable Cutting</span>
                    </div>
                    <div className="flex items-center gap-4">
                        <ThemeToggle />
                        <Link to="/" data-testid="cutting-back-link" className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-mist hover:text-copper transition-colors duration-300">
                            <ArrowLeft size={14} /> Site
                        </Link>
                    </div>
                </div>
            </header>

            <div className="max-w-[1100px] mx-auto px-4 sm:px-8 py-14">
                <Eyebrow className="mb-5">Tools — Cable Cutting</Eyebrow>
                <h1 className="font-display font-bold tracking-tight leading-[1.05] text-4xl md:text-5xl text-ink mb-4">
                    Exact lengths, <em className="text-copper">zero guesswork.</em>
                </h1>
                <p className="text-mist max-w-2xl mb-12">
                    Pick a cable, list the lengths you need, and our planner works out which drums can supply
                    each continuous cut — no joined pieces unless you approve it. Our desk confirms final
                    availability and pricing.
                </p>

                {done ? (
                    <Reveal>
                        <div className="rounded-md border border-line bg-white p-10 text-center" data-testid="cutting-success">
                            <p className="font-display font-semibold text-2xl text-ink mb-2">Cutting enquiry received</p>
                            <p className="text-mist mb-6">
                                {done.total_length} m across your listed cuts — reference{" "}
                                <span className="font-mono text-copper">{done.enquiry_id.slice(-8).toUpperCase()}</span>.
                                Our desk will confirm drum availability and share a quotation.
                            </p>
                            <Link to="/" className="rounded-full bg-copper text-white font-mono uppercase tracking-[0.18em] text-[11px] px-8 py-3.5 hover:bg-copper-bright transition-colors" data-testid="cutting-success-home">
                                Back to site
                            </Link>
                        </div>
                    </Reveal>
                ) : (
                    <div className="grid lg:grid-cols-2 gap-6 items-start">
                        <Reveal>
                            <div className="rounded-md border border-line bg-white p-6 md:p-8">
                                <div className="mb-5">
                                    <label className={labelCls}>Cable product</label>
                                    <select data-testid="cutting-product-select" value={sku} onChange={(e) => { setSku(e.target.value); setPlan(null); }} className={`${inputCls} appearance-none`}>
                                        <option value="">Select product…</option>
                                        {products.map((p) => (
                                            <option key={p.sku} value={p.sku}>{p.name} ({p.sku})</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="mb-5">
                                    <label className={labelCls}>Indicative rate (₹ / metre)</label>
                                    <input data-testid="cutting-rate-input" type="number" min="0" step="any" value={rate} onChange={(e) => setRate(e.target.value)} className={inputCls} />
                                    <p className="font-mono text-[9px] text-dim mt-1.5 uppercase tracking-[0.1em]">Illustrative only — final rate is confirmed on your quotation</p>
                                </div>

                                <label className={labelCls}>Required cuts (continuous lengths)</label>
                                <div className="space-y-2 mb-3">
                                    {cuts.map((c, i) => (
                                        <div key={i} className="flex gap-2 items-center" data-testid={`cutting-row-${i}`}>
                                            <input data-testid={`cutting-length-${i}`} type="number" min="0" step="any" value={c.length} onChange={(e) => setCut(i, "length", e.target.value)} placeholder="Length (m)" className={inputCls} />
                                            <span className="font-mono text-xs text-dim">×</span>
                                            <input data-testid={`cutting-qty-${i}`} type="number" min="1" max="50" value={c.qty} onChange={(e) => setCut(i, "qty", e.target.value)} className={`${inputCls} !w-20`} />
                                            <button data-testid={`cutting-delete-${i}`} onClick={() => setCuts((p) => p.filter((_, idx) => idx !== i))} className="text-dim hover:text-red-600" aria-label="Remove row">
                                                <Trash2 size={14} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                                <div className="flex items-center justify-between mb-6">
                                    <button data-testid="cutting-add-row" onClick={() => setCuts((p) => [...p, { length: "", qty: "1" }])} className="rounded-full border border-line text-mist font-mono text-[10px] uppercase tracking-[0.18em] px-4 py-2 hover:border-copper hover:text-copper transition-colors flex items-center gap-2">
                                        <Plus size={12} /> Add cut
                                    </button>
                                    <span className="font-mono text-xs text-mist" data-testid="cutting-total">
                                        Total: <span className="text-ink font-semibold">{total.toLocaleString("en-IN")} m</span>
                                    </span>
                                </div>

                                <button data-testid="cutting-plan-btn" onClick={runPlan} disabled={planning} className="w-full rounded-full bg-ink text-white font-mono uppercase tracking-[0.18em] text-[11px] py-4 hover:bg-copper transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                                    <Scissors size={14} /> {planning ? "Planning…" : "Plan my cuts"}
                                </button>
                            </div>
                        </Reveal>

                        <Reveal delay={0.1}>
                            <div className="rounded-md border border-line bg-white p-6 md:p-8" data-testid="cutting-plan-panel">
                                {!plan ? (
                                    <div className="text-center py-10">
                                        <Scissors size={26} className="text-copper mx-auto mb-4" />
                                        <p className="text-mist text-sm">Your drum allocation plan appears here.</p>
                                    </div>
                                ) : (
                                                    <>
                                        <p className={`font-mono text-[10px] uppercase tracking-[0.2em] mb-4 ${plan.availability === "pending_confirmation" ? "text-amber-700" : "text-emerald-700"}`} data-testid="cutting-availability">
                                            {plan.availability === "pending_confirmation" ? "Availability Pending Confirmation" : plan.fully_covered ? "All cuts covered by drum register" : "Partial coverage — desk will confirm balance"}
                                        </p>
                                        <p className="text-mist text-sm mb-4">{plan.note}</p>
                                        {plan.drums_used?.length > 0 && (
                                            <div className="space-y-3 mb-4">
                                                {plan.drums_used.map((d) => (
                                                    <div key={d.drum_id} className="rounded-md border border-line bg-paper2 p-4" data-testid={`cutting-drum-${d.drum_id}`}>
                                                        <div className="flex flex-wrap justify-between gap-2 mb-2">
                                                            <span className="font-mono text-xs text-ink font-semibold">{d.drum_id} <span className="text-dim">({d.status})</span></span>
                                                            <span className="font-mono text-[10px] text-dim">{d.location}</span>
                                                        </div>
                                                        <p className="font-mono text-[11px] text-mist">Cuts: {d.cuts.map((c) => `${c} m`).join(" + ")}</p>
                                                        <p className="font-mono text-[11px] mt-1">
                                                            <span className="text-dim">Leftover {d.leftover} m → </span>
                                                            <span className={d.leftover_class === "reusable_balance" ? "text-emerald-700" : "text-amber-700"}>
                                                                {d.leftover_class === "reusable_balance" ? "reusable balance" : "scrap (below 10 m)"}
                                                            </span>
                                                        </p>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        {plan.unmet_cuts?.length > 0 && (
                                            <p className="font-mono text-[11px] text-amber-700 mb-4" data-testid="cutting-unmet">
                                                Not coverable from register: {plan.unmet_cuts.map((c) => `${c} m`).join(", ")} — desk will advise.
                                            </p>
                                        )}

                                        {parseFloat(rate) > 0 && (
                                            <div className="rounded-md bg-paper2 border border-line p-4 mb-5 font-mono text-xs space-y-1.5" data-testid="cutting-estimate">
                                                <p className="flex justify-between text-mist"><span>SALEABLE LENGTH</span><span className="text-ink">{plan.total_length.toLocaleString("en-IN")} m</span></p>
                                                <p className="flex justify-between text-mist"><span>CABLE VALUE @ ₹{parseFloat(rate).toLocaleString("en-IN")}/m</span><span className="text-ink" data-testid="cutting-est-value">₹ {(plan.total_length * parseFloat(rate)).toLocaleString("en-IN")}</span></p>
                                                <p className="flex justify-between text-mist"><span>GST @ 18%</span><span className="text-ink" data-testid="cutting-est-gst">₹ {(plan.total_length * parseFloat(rate) * 0.18).toLocaleString("en-IN")}</span></p>
                                                <p className="flex justify-between text-ink font-bold pt-1.5 border-t border-line"><span>ESTIMATED TOTAL</span><span className="text-copper" data-testid="cutting-est-total">₹ {(plan.total_length * parseFloat(rate) * 1.18).toLocaleString("en-IN")}</span></p>
                                                <p className="text-dim text-[9px] uppercase tracking-[0.1em] pt-1">Illustrative — rate confirmed on quotation</p>
                                            </div>
                                        )}

                                        <form onSubmit={submit} className="border-t border-line pt-5 space-y-3" data-testid="cutting-contact-form">
                                            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-dim">Send this cutting enquiry</p>
                                            <div className="grid sm:grid-cols-2 gap-3">
                                                <input data-testid="cutting-name" required value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} placeholder="Your name *" className={inputCls} />
                                                <input data-testid="cutting-phone" required value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} placeholder="Phone *" className={inputCls} />
                                                <input data-testid="cutting-company" value={contact.company} onChange={(e) => setContact({ ...contact, company: e.target.value })} placeholder="Company" className={inputCls} />
                                                <input data-testid="cutting-email" type="email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} placeholder="Email" className={inputCls} />
                                            </div>
                                            <input data-testid="cutting-note" value={contact.note} onChange={(e) => setContact({ ...contact, note: e.target.value })} placeholder="Site notes (optional)" className={inputCls} />
                                            <button data-testid="cutting-submit-btn" disabled={submitting} className="w-full rounded-full bg-copper text-white font-mono uppercase tracking-[0.18em] text-[11px] py-4 hover:bg-copper-bright transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                                                <Send size={13} /> {submitting ? "Sending…" : "Submit cutting enquiry"}
                                            </button>
                                        </form>
                                    </>
                                )}
                            </div>
                        </Reveal>
                    </div>
                )}
            </div>
        </div>
    );
}
